import { CSSProperties, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconAngleUp, IconDots, IconSort } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { t } from '@lingui/core/macro';
import { Header, flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import { ApplicationStatusInfo } from 'components/ApplicationStatusInfo/ApplicationStatusInfo';
import { Drawer } from 'components/Drawer/Drawer';
import { TApplication } from 'lendingApi/applications.types';
import { APPLICATION_TABLE_KEYS } from 'utils/applications/applications.config';
import { COLUMN_IDS } from '../../Context/ApplicationsTableState.helpers';
import { Filter } from '../../Filter/Filter';
import styles from './DraggableTableHeader.module.css';

type TProps = {
  header: Header<TApplication, unknown>;
};

export const DraggableTableHeader = ({ header }: TProps) => {
  const dragDisabled = header.column.columnDef.meta?.draggable === false;
  const [isShowingStatusDrawer, setIsShowingStatusDrawer] = useState(false);

  const toggleStatusDrawer = () => setIsShowingStatusDrawer((prev) => !prev);

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
      disabled: dragDisabled,
    });

  const baseStyles: CSSProperties = {
    width: header.getSize(),
    opacity: isDragging ? 0.8 : 1,
    transform: CSS.Translate.toString(transform),
    transition: 'width transform 0.2s ease-in-out',
    zIndex: isDragging ? 1 : 0,
  };

  const sortState = header.column.getIsSorted();

  const renderSortButton = () => {
    const renderIcon = () => {
      if (sortState === false) return <IconSort />;
      if (sortState === 'asc') return <IconAngleUp width={16} />;
      if (sortState === 'desc')
        return <IconAngleUp className={styles.downIcon} width={16} />;
    };

    return (
      <button
        id="column-sort-icon"
        className={styles.sortButton}
        onClick={header.column.getToggleSortingHandler()}
      >
        {renderIcon()}
      </button>
    );
  };

  const renderHeaderContent = () => {
    const headerText = flexRender(
      header.column.columnDef.header,
      header.getContext(),
    );
    const renderFilter = () => {
      return header.column.getCanFilter() && <Filter column={header.column} />;
    };

    if (header.column.id === COLUMN_IDS.status) {
      return (
        <>
          <Button
            onClick={toggleStatusDrawer}
            className={clsx(styles.headerText, styles.statusButton)}
            id="status-guide"
            variant="tertiary"
          >
            {headerText}
          </Button>
          {renderFilter()}
        </>
      );
    }
    if (header.column.getCanSort() || header.column.getCanFilter()) {
      return (
        <>
          {headerText}
          <div className={styles.buttonsContainer}>
            {header.column.getCanSort() && renderSortButton()}
            {renderFilter()}
          </div>
        </>
      );
    }

    return <span className={styles.headerText}>{headerText}</span>;
  };

  return (
    <>
      <th
        data-testid="draggable-table-header"
        id={`${header.column.id}-column`}
        colSpan={header.colSpan}
        ref={setNodeRef}
        className={styles.tableHeaderItem}
        style={baseStyles}
      >
        <div className={styles.tableHeaderContent}>
          {!dragDisabled && (
            <button
              id="drag-and-drop-button"
              {...attributes}
              {...listeners}
              className={clsx(styles.dragAndDropButton, {
                [styles.isDragging]: isDragging,
              })}
            >
              <IconDots className={styles.dragAndDropIcon} />
            </button>
          )}

          <div className={styles.headerContainer}>{renderHeaderContent()}</div>
        </div>
      </th>

      {header.column.id === APPLICATION_TABLE_KEYS.status && (
        <Drawer
          title={t`Status guide`}
          show={isShowingStatusDrawer}
          onClose={toggleStatusDrawer}
        >
          <ApplicationStatusInfo />
        </Drawer>
      )}
    </>
  );
};
